import { FC, useState } from 'react';

import { useQuery } from '@apollo/client';
import {
    Button,
    CircularProgress,
    Grid,
    InputAdornment,
    TextField,
    Typography,
} from '@mui/material';
import { AddMerchantApplication, ApplicationStatus } from '@prisma/client';
import { ErrorMessage, Field, FieldArray, Formik } from 'formik';
import * as Yup from 'yup';

import { useMutation } from '@redwoodjs/web';

import { TextAreaInput } from 'src/components';
import NPIButton from 'src/components/Button';
import { CustomInput } from 'src/components/CustomInput';
import { ModalBox } from 'src/components/ModalContent/Modal';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import { SEND_MERCHANT_APPLICATION_ERROR } from 'src/graphql/mutations/sendMerchantApplicationError.mutation';
import { GET_USER } from 'src/graphql/queries/users.query';

interface ErrorDetailsModalProps {
    isModalOpen: boolean;
    handleCloseModal: (value: boolean) => void;
    merchantApplication: AddMerchantApplication;
    onApplicationStatusUpdate: (status: ApplicationStatus) => void;
}

const validationSchema = Yup.object().shape({
    emailTo: Yup.string().required(),
    emailCc: Yup.string().optional(),
    emailSubject: Yup.string().required(),
    emailMessage: Yup.string().required('Description is required'),
});

const ErrorDetailsModal: FC<ErrorDetailsModalProps> = ({
    isModalOpen,
    handleCloseModal,
    merchantApplication,
    onApplicationStatusUpdate,
}) => {
    const [loading, setLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { data: userData } = useQuery(GET_USER, {
        variables: { id: merchantApplication?.authorId },
        skip: !merchantApplication?.authorId,
    });

    const [sendMerchantApplicationError] = useMutation(SEND_MERCHANT_APPLICATION_ERROR, {
        onCompleted: () => {
            setIsSubmitting(true);
            showSuccessToast('Your email was successfully sent to the seller');
            handleCloseModal(true);
        },
    });

    const handleFileUpload = async (
        file: File
    ): Promise<{ fileName: string; fileType: string; data: string }> => {
        const reader = new FileReader();
        return new Promise((resolve, reject) => {
            reader.onload = () => {
                const base64Data = reader.result?.toString().split(',')[1] || '';
                resolve({
                    fileName: file.name,
                    fileType: file.type,
                    data: base64Data,
                });
            };
            reader.onerror = (error) => {
                reject(error);
            };
            reader.readAsDataURL(file);
        });
    };

    const handleEmailSubmit = async (values) => {
        try {
            setLoading(true);
            const attachments = await Promise.all(
                values.attachments.map(async (file) => {
                    const uploadedFile = await handleFileUpload(file);
                    return {
                        fileName: uploadedFile.fileName,
                        fileType: uploadedFile.fileType,
                        data: uploadedFile.data,
                    };
                })
            );

            await sendMerchantApplicationError({
                variables: {
                    input: {
                        emailTo: userData?.user.email,
                        emailCc: values.emailCc,
                        emailSubject: values.emailSubject,
                        emailMessage: values.emailMessage,
                        applicationDocuments: attachments,
                        seller: userData.user.firstName,
                        merchantApplicationId: merchantApplication?.id.toString(),
                        merchantTradingName:
                            merchantApplication?.tradingName ||
                            merchantApplication?.merchantInformation?.aboutBusiness?.tradingName,
                    },
                },
            });

            onApplicationStatusUpdate(ApplicationStatus.ERROR_IDENTIFIED);
            setLoading(false);
        } catch (error) {
            showErrorToast('There was an error sending the email.');
            setLoading(false);
        }
    };

    return (
        <Formik
            initialValues={{
                emailTo: '',
                emailSubject: `Error Identified with buddy Application ID: ${merchantApplication?.merchantAccountNumber}`,
                emailCc: `${process.env.CUSTOMER_SERVICE_EMAIL}; `,
                emailMessage: '',
                attachments: [],
            }}
            onSubmit={handleEmailSubmit}
            validationSchema={validationSchema}
            enableReinitialize
        >
            {({ values, setFieldValue, handleSubmit }) => {
                const handleFileSelect = () => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.onchange = (event) => {
                        const file = event.target.files[0];
                        if (file) {
                            setFieldValue('attachments', [...values.attachments, file]);
                        }
                    };
                    input.click();
                };

                return (
                    <>
                        <ModalBox
                            open={isModalOpen}
                            handleCloseModal={handleCloseModal}
                            title={'Error Details'}
                            widthSize="800px"
                        >
                            <Grid
                                container
                                spacing={2}
                                sx={{
                                    marginTop: '5px',
                                }}
                            >
                                <Grid item xs={12}>
                                    <CustomInput
                                        label={`Send To`}
                                        value={`${userData?.user.firstName} ${userData?.user.lastName}  <${userData?.user.email}>`}
                                        readOnly
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Field
                                        as={CustomInput}
                                        label="CC"
                                        name="emailCc"
                                        value={values.emailCc}
                                        fullWidth
                                    />
                                    <ErrorMessage name="emailCc" component="div" />
                                </Grid>
                                <Grid item xs={12}>
                                    <CustomInput
                                        label={`Subject:`}
                                        name="emailSubject"
                                        value={`Error Identified with buddy Application ID: ${merchantApplication?.merchantAccountNumber}`}
                                        readOnly
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextAreaInput
                                        name="emailMessage"
                                        placeholder="Enter detials about error identified with application"
                                        label="Description"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="h5">Attachments?</Typography>{' '}
                                </Grid>
                                <FieldArray name="attachments">
                                    {(arrayHelpers) => {
                                        return (
                                            <Grid item xs={12} spacing={2}>
                                                {values.attachments.map((file, index) => {
                                                    return (
                                                        <Grid
                                                            key={index}
                                                            style={{ marginBottom: '20px' }}
                                                        >
                                                            <TextField
                                                                value={
                                                                    file.name || 'No Selected file'
                                                                }
                                                                readOnly
                                                                fullWidth
                                                                InputProps={{
                                                                    readOnly: true,
                                                                    endAdornment: (
                                                                        <InputAdornment position="end">
                                                                            <NPIButton
                                                                                onClick={() => {
                                                                                    return arrayHelpers.remove(
                                                                                        index
                                                                                    );
                                                                                }}
                                                                                label="Remove"
                                                                            />
                                                                        </InputAdornment>
                                                                    ),
                                                                }}
                                                            />
                                                        </Grid>
                                                    );
                                                })}
                                                {values.attachments.length < 5 && (
                                                    <Grid item xs={12} spacing={2}>
                                                        <TextField
                                                            value="Select File"
                                                            fullWidth
                                                            InputProps={{
                                                                readOnly: true,
                                                                endAdornment: (
                                                                    <InputAdornment position="end">
                                                                        <NPIButton
                                                                            onClick={() => {
                                                                                return handleFileSelect(
                                                                                    values,
                                                                                    setFieldValue
                                                                                );
                                                                            }}
                                                                            label="Select File"
                                                                        />
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                        />
                                                    </Grid>
                                                )}
                                            </Grid>
                                        );
                                    }}
                                </FieldArray>
                                <Grid item xs={12} textAlign="right">
                                    <Button
                                        variant="contained"
                                        type="submit"
                                        disabled={!values.emailMessage && loading}
                                        className="btn-medium-with-icon btn-dark"
                                        onClick={() => {
                                            handleEmailSubmit(values);
                                        }}
                                    >
                                        {loading ? <CircularProgress size={24} /> : 'Send'}
                                    </Button>
                                </Grid>
                            </Grid>
                        </ModalBox>
                    </>
                );
            }}
        </Formik>
    );
};

export default ErrorDetailsModal;
